/**
 * 반응률·평균·상위 백분위.
 *
 * 전부 「가장 최근 수집일」 스냅샷(`getMetricsByLatestDate`) 위에서 센다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

import { getPopularityScore } from 'src/utils/metrics/popularity-score';
import { engagementRatePctOf } from 'src/utils/metrics/engagement-rate';
import { isMetricCountField } from 'src/utils/metrics/metric-field-keys';
import { getMetricsByLatestDate } from 'src/utils/metrics/post-metric-snapshot';

/**
 * 여러 게시물에 대해, 각 게시물의 메트릭 최신 날짜 기준 Count 값들의 평균을 구함
 * @param {Array<Object>} posts - 게시물 배열 (각각 metrics 또는 likeCount 등 필드 보유)
 * @returns {Object} 최신 날짜 기준 평균값 { avgLikeCount, avgCommentCount, avgViewCount, avgShareCount, avgSavedCount, avgDownloadCount }
 *
 * @example
 * getAverageMetricsByLatestDate(posts)
 * // { avgLikeCount: 1234.5, avgCommentCount: 56.2, avgViewCount: 20000000, ... }
 */
export function getAverageMetricsByLatestDate(posts: SocialPostInput[] | null | undefined) {
  const empty = {
    avgLikeCount: 0,
    avgCommentCount: 0,
    avgViewCount: 0,
    avgShareCount: 0,
    avgSavedCount: 0,
    avgDownloadCount: 0,
  };
  if (!Array.isArray(posts) || posts.length === 0) return empty;

  let sumLike = 0;
  let sumComment = 0;
  let sumView = 0;
  let sumShare = 0;
  let sumSaved = 0;
  let sumDownload = 0;

  posts.forEach((post) => {
    const m = getMetricsByLatestDate(post);
    sumLike += m.likeCount ?? 0;
    sumComment += m.commentCount ?? 0;
    sumView += m.viewCount ?? 0;
    sumShare += m.shareCount ?? 0;
    sumSaved += m.savedCount ?? 0;
    sumDownload += m.downloadCount ?? 0;
  });

  const n = posts.length;
  return {
    avgLikeCount: sumLike / n,
    avgCommentCount: sumComment / n,
    avgViewCount: sumView / n,
    avgShareCount: sumShare / n,
    avgSavedCount: sumSaved / n,
    avgDownloadCount: sumDownload / n,
  };
}

/**
 * 단일 게시물의 반응률(%) — 최신 메트릭 날짜 기준 · 셈은 `engagementRatePct` 하나다.
 * @returns 조회수 0이면 0(백분위 비교용)
 */
function getEngagementRateByLatestDate(post: SocialPostInput | null | undefined): number {
  return engagementRatePctOf(getMetricsByLatestDate(post)) ?? 0;
}

/**
 * 단일 게시물의 반응률(%) — 정렬용. 최신 metrics 우선(`getMetricsByLatestDate`) · 셈은 `engagementRatePct`.
 * @returns 조회수 0 이하면 `null`(정렬에서 항상 뒤로)
 */
export function getEngagementRatePercentForSort(
  post: SocialPostInput | null | undefined,
): number | null {
  return engagementRatePctOf(getMetricsByLatestDate(post));
}

/** 상위 % 계산 시 사용 가능한 반응 지표 키 */
export const REACTION_METRIC_KEYS = {
  LIKE: 'likeCount',
  COMMENT: 'commentCount',
  VIEW: 'viewCount',
  SHARE: 'shareCount',
  SAVED: 'savedCount',
  DOWNLOAD: 'downloadCount',
  ER: 'er',
  /** 게시물 종합 점수 (metricScore 필드 또는 like+comment+view 합산) */
  METRIC_SCORE: 'metricScore',
};

/**
 * 게시물 하나에 대해 지정한 반응 지표값 추출 (최신 메트릭 날짜 기준)
 * @param {Object} post - 게시물 객체
 * @param {string} metricKey - REACTION_METRIC_KEYS 값 (likeCount, commentCount, viewCount, shareCount, savedCount, downloadCount, er)
 * @returns {number}
 */
function getMetricValueByLatestDate(
  post: SocialPostInput | null | undefined,
  metricKey: string,
): number {
  if (metricKey === REACTION_METRIC_KEYS.ER) {
    return getEngagementRateByLatestDate(post);
  }
  if (metricKey === REACTION_METRIC_KEYS.METRIC_SCORE) {
    return getPopularityScore(post);
  }
  const m = getMetricsByLatestDate(post);
  const v = isMetricCountField(metricKey) ? m[metricKey] : undefined;
  return v != null ? Number(v) : 0;
}

/**
 * 전체 게시물 중 비교 대상 게시물이 "상위 몇 %"인지 계산 (최신 메트릭 날짜 기준)
 * @param {Array<Object>} posts - 전체 게시물 배열
 * @param {Object} targetPost - 비교하고자 하는 게시물
 * @param {string} metricKey - 반응 지표 (REACTION_METRIC_KEYS: likeCount, commentCount, viewCount, shareCount, savedCount, downloadCount, er)
 * @returns {number} 상위 퍼센트 (0~100). 예: 20 이면 "상위 20%"
 *
 * @example
 * getTopPercentileByLatestDate(allPosts, myPost, 'er')   // 15 → 상위 15%
 * getTopPercentileByLatestDate(allPosts, myPost, 'likeCount')
 */
export function getTopPercentileByLatestDate(
  posts: SocialPostInput[] | null | undefined,
  targetPost: SocialPostInput | null | undefined,
  metricKey: string,
): number {
  if (!Array.isArray(posts) || posts.length === 0) return 0;
  const targetValue = getMetricValueByLatestDate(targetPost, metricKey);
  let countAboveOrEqual = 0;
  posts.forEach((post) => {
    const value = getMetricValueByLatestDate(post, metricKey);
    if (value >= targetValue) countAboveOrEqual += 1;
  });
  return Math.round((countAboveOrEqual / posts.length) * 100);
}
